import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';

import { CUENTA_DEMO, SESION_DEMO } from '../data/auth.data';
import { Session } from '../models/session.model';

/** Resultado de un intento de acceso. */
export interface IResultadoAcceso {
  mensaje: string;
  ok: boolean;
}

/** Clave del almacenamiento. La comparten `localStorage` y `sessionStorage`. */
const CLAVE_SESION = 'cleversec-nps-session';

/**
 * Retraso simulado del acceso.
 *
 * Sin él el botón parpadea y el estado «Entrando…» no se llega a ver, así que
 * un fallo de credenciales se siente como si el botón no hubiera respondido.
 * 520ms es el tiempo que tarda una llamada real razonable.
 */
const RETRASO_MS = 520;

/**
 * Acceso de la demo.
 *
 * **No es seguridad, es una puerta.** No hay backend, así que la comprobación
 * ocurre en el navegador contra `data/auth.data.ts` y cualquiera puede saltársela
 * escribiendo en el almacenamiento. Existe para que la demo se presente como el
 * producto —con su pantalla de acceso y su identidad arriba a la derecha—, no
 * para proteger nada. El día que haya API, lo único que cambia es el cuerpo de
 * `login()`: la sesión, los guardias y las pantallas ya están en su sitio.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {

  private readonly sesionActiva: WritableSignal<Session | null> = signal<Session | null>(this.leerSesion());

  /** Sesión vigente. La leen la barra superior y el panel lateral. */
  public readonly session: Signal<Session | null> = this.sesionActiva.asReadonly();

  /** Atajo para los guardias y las plantillas. */
  public readonly autenticado: Signal<boolean> = computed<boolean>(() => this.sesionActiva() !== null);

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR API PÚBLICA                            */
  /* -------------------------------------------------------------------------- */

  /**
   * Valida las credenciales de la demo.
   *
   * `recordarme` decide dónde vive la sesión: en `localStorage` sobrevive a
   * cerrar el navegador, en `sessionStorage` muere con la pestaña. Es la
   * diferencia entre una laptop propia y la sala de juntas del cliente.
   */
  public login(correo: string, contrasena: string, recordarme: boolean): Promise<IResultadoAcceso> {
    return new Promise<IResultadoAcceso>((resolve) => {
      setTimeout(() => {
        const limpio: string = correo.trim().toLowerCase();

        if (limpio !== CUENTA_DEMO.correo || contrasena !== CUENTA_DEMO.contrasena) {
          // Un solo mensaje para los dos errores: decir cuál de los dos campos
          // falló le confirma a quien prueba a ciegas que el correo existe.
          resolve({ ok: false, mensaje: 'El correo o la contraseña no coinciden. Revísalos e inténtalo de nuevo.' });

          return;
        }

        const sesion: Session = new Session(SESION_DEMO);

        this.guardarSesion(sesion, recordarme);
        this.sesionActiva.set(sesion);

        resolve({ ok: true, mensaje: '' });
      }, RETRASO_MS);
    });
  }

  /** Cierra la sesión y limpia los dos almacenamientos. */
  public logout(): void {
    this.sesionActiva.set(null);

    try {
      localStorage.removeItem(CLAVE_SESION);
      sessionStorage.removeItem(CLAVE_SESION);
    } catch {
      /* modo privado o almacenamiento bloqueado: la sesión sólo vivía en memoria */
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR INTERNO                              */
  /* -------------------------------------------------------------------------- */

  /** `localStorage` primero: es el que el usuario pidió que durara. */
  private leerSesion(): Session | null {
    for (const almacen of this.almacenes()) {
      const crudo: string | null = almacen.getItem(CLAVE_SESION);

      if (!crudo) {
        continue;
      }

      try {
        const sesion: Session | null = Session.desde(JSON.parse(crudo));

        if (sesion) {
          return sesion;
        }
      } catch {
        /* JSON corrupto: se descarta y se sigue con el otro almacén */
      }

      almacen.removeItem(CLAVE_SESION);
    }

    return null;
  }

  private guardarSesion(sesion: Session, recordarme: boolean): void {
    try {
      // Se limpia el otro almacén antes de escribir: si no, alternar «Recordarme»
      // deja dos sesiones y la de `localStorage` gana para siempre.
      localStorage.removeItem(CLAVE_SESION);
      sessionStorage.removeItem(CLAVE_SESION);

      const destino: Storage = recordarme ? localStorage : sessionStorage;

      destino.setItem(CLAVE_SESION, JSON.stringify(sesion));
    } catch {
      /* la sesión vive sólo en memoria: al recargar habrá que volver a entrar */
    }
  }

  /** Envuelto en try: en Safari con cookies bloqueadas, tocarlos lanza. */
  private almacenes(): Array<Storage> {
    try {
      return [localStorage, sessionStorage];
    } catch {
      return [];
    }
  }
}
